import React, { useEffect, useRef, useState } from 'react'
import * as ECT from '@whoicd/icd11ect';
import '@whoicd/icd11ect/style.css';
import { getTokenWho } from '../../../../services';
import PropTypes from 'prop-types';
import { Translate, withLocalize } from 'react-localize-redux';
import { Grid, TextField } from '@mui/material';
import { FieldWrapper, IconGenerator } from '../../mini_components';
import styled from 'styled-components';

/**
 * SHER-545 — a well-formed JWT whose payload is exactly `{"exp":0}`.
 *
 * Handed to ECT when no real token is available. It decodes cleanly, so it does not
 * trip the `InvalidTokenError` this ticket is about, and its expiry resolves to 1969,
 * so ECT treats it as stale and re-requests a token on the very next search.
 *
 * The `exp` claim is load-bearing: with a payload of `{}` the expiry becomes
 * `Invalid Date`, `Date.now() > NaN` is false, and ECT would then never ask for a
 * token again — trading the crash for a silently dead lookup. Do not trim it.
 */
const EXPIRED_TOKEN_SENTINEL = 'e30.eyJleHAiOjB9.sherwood-placeholder';

const RedoHolder = styled.div`
    display: flex;
    align-items: center;
    padding-left: 1rem;
`
function ICTSelectorOMS(props){
    const [show, setShow] = useState(false);
    const [textDiagnose, setTextDiagnose] = useState("");
    // SHER-545: the last token WHO actually gave us. ECT decodes whatever
    // getNewTokenFunction returns, so a stale-but-real JWT is the only safe
    // fallback — it parses, ECT sees `exp` has lapsed and simply asks again.
    const lastKnownTokenRef = useRef(null);
    function redoSearch(){
        console.log("Redo Search")
        setTextDiagnose("");
        props.resetICTSelectorCallback();
    }
    function onChange(event){
        console.log(event.target.value);
        setTextDiagnose(event.target.value);
    }
    useEffect(() => {
        const mySettings = {
            apiServerUrl: "https://id.who.int",   
            apiSecured: true,
            icdMinorVersion: "2022-02" ,
            icdLinearization: "mms",
            language: props.language,
            sourceApp: "Sherwood",
            wordsAvailable: false,
            chaptersAvailable: true,
            flexisearchAvailable: true,
            autoBind:false,
            height: "500px"
        };
        const myCallbacks = {
            searchStartedFunction: () => {
                //this callback is called when searching is started.
                console.log("Search started!");
            },
            searchEndedFunction: () => {
                //this callback is called when search ends.
                console.log("Search ended!");
            },
            selectedEntityFunction: (selectedEntity) => {
            //This callback is called when the user makes a selection
            //This is the best way to get what the user has chosen and use it in 
            //your application
                console.log('selected uri: '+ selectedEntity.uri);
                console.log('selected code: '+ selectedEntity.code);
                console.log('selected bestMatchText: '+ selectedEntity.bestMatchText);
                ECT.Handler.clear("1");
                setTextDiagnose(selectedEntity.title);
                
    
                if(selectedEntity){
                    const tempValue = {
                        "ict" : selectedEntity.title,
                        "ict-code" : selectedEntity.code
                    }
                   
                   
                    props.elementSelected(tempValue);    
                }
                
            },
            getNewTokenFunction: async () => {
                // if the embedded coding tool is working with the cloud hosted ICD-API, you need to set apiSecured=true
                // In this case embedded coding tool calls this function when it needs a new token.
                // In this case you backend web application should provide updated tokens
                //
                // SHER-545: this callback must ALWAYS resolve to a decodable JWT string. ECT takes
                // what it resolves to and decodes it on the spot, outside any try, to read `exp`
                // (`d = await getNewTokenFunction(); this.token = d; f = jwtDecode(d); p = f.exp`),
                // and its bundled jwt-decode opens with
                // `if (typeof token !== "string") throw InvalidTokenError("Invalid token specified")`,
                // then rethrows the same error if the payload segment will not parse. So anything
                // it cannot decode, leaked from here, became the unhandled rejection reported as
                // sig-56e0b17d and left the clinician with a dead ICD lookup.
                //
                // It leaked one in three ways: getTokenWho REJECTS on a non-2xx (the API answers a
                // WHO outage with res.status(500)), the `if` had no else so a non-200 body fell
                // through to an implicit `undefined`, and a 200 body without an access_token
                // returned `undefined` too. All three are contained here now.
                try {
                    const response = await getTokenWho(props.translate("lang"));
                    // Three segments, because a non-empty string is not enough: a body carrying
                    // something like token:"unauthorized" would sail through and hit the *parse*
                    // branch of the same jwt-decode guard this ticket is about.
                    if(response && response.status === 200 && typeof response.token === 'string' && response.token.split('.').length === 3){
                        lastKnownTokenRef.current = response.token;
                        return response.token;
                    }
                    console.log(`ICT token unusable: status=${response?.status ?? 'no-response'} tokenType=${typeof response?.token}`);
                }
                catch(error){
                    // Shape only, never the value — a token is a credential (SHER-545).
                    console.log('ICT token request failed', error?.response?.status ?? error?.message ?? 'unknown');
                }
                // Prefer the last token WHO actually gave us; fall back to a token that is merely
                // well-formed and already expired. Either way ECT gets something it can decode,
                // sees a lapsed `exp`, and asks again on the next search — so the lookup heals by
                // itself once WHO recovers, instead of being disabled for the life of the page.
                return lastKnownTokenRef.current ?? EXPIRED_TOKEN_SENTINEL;
            }
        };
        ECT.Handler.configure(mySettings, myCallbacks);
        ECT.Handler.bind("1");

    }, [show])
    
    const value = props.value ? props.value : textDiagnose;
    return ([
        <FieldWrapper>
            <div style={{display:'flex'}}>
                <TextField key="ict-input" {...props} label={props.label || props.translate("hospital.select-ict")} value = {value} color="secondary"
                    inputProps={{className : "ctw-input", "data-ctw-ino" : "1"}} fullWidth 
                    onChange={(event) => onChange(event) } />
                {
                    props.resetICTSelectorCallback && 
                    <RedoHolder onClick={redoSearch} >
                        <IconGenerator type="undo" /> 
                    </RedoHolder>
                }
            </div>
        </FieldWrapper>,
        <div key="ict-container" className="ctw-window" data-ctw-ino="1"></div>
    
    ]
        
    )
}

ICTSelectorOMS.propTypes = {
    typeUser:PropTypes.string
}

export default withLocalize(ICTSelectorOMS)